import React, { useState } from 'react';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/modal';
import { Upload, FileUp, CheckCircle, AlertCircle, Loader2, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/useAuth';
import { ProjectService } from '../lib/projects';
import { AuthModal } from './AuthModal';

interface FileUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const FileUploadModal: React.FC<FileUploadModalProps> = ({
  open,
  onOpenChange
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [projectName, setProjectName] = useState<string>('');
  const [projectDescription, setProjectDescription] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const projectService = ProjectService.getInstance();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      setFile(selectedFile);
      setError(null);
      
      // Auto-generate project name from filename if not provided
      if (!projectName) {
        const nameWithoutExtension = selectedFile.name.replace(/\.[^/.]+$/, "");
        setProjectName(nameWithoutExtension);
      }
    }
  };

  const handleProcessDocument = async () => {
    if (!isAuthenticated) {
      setIsAuthModalOpen(true);
      return;
    }

    if (!file || !projectName.trim()) {
      setError('Please select a file and provide a project name.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const result = await projectService.createProject({
        name: projectName.trim(),
        description: projectDescription.trim() || undefined,
        file: file
      });

      if (result.success && result.project) {
        onOpenChange(false);
        navigate(`/projects/${result.project.id}`);
      } else {
        throw new Error(result.error || 'Failed to create project');
      }

    } catch (err: any) {
      setError(err.message || 'Failed to upload and process document. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setProjectName('');
    setProjectDescription('');
    setError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(newOpen) => {
        if (!newOpen) {
          resetForm();
        }
        onOpenChange(newOpen);
      }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-foreground">
                              Index New Book
            </DialogTitle>
                          <DialogDescription className="text-muted-foreground">
              {isAuthenticated 
                ? "Upload your book or manuscript to begin creating your index."
                : "Please sign in to create a new book index."
              }
            </DialogDescription>
          </DialogHeader>

          {!isAuthenticated ? (
            <div className="text-center py-8">
                              <User className="mx-auto text-muted-foreground mb-4" size={48} />
                <h3 className="text-lg font-medium text-foreground mb-2">Sign In Required</h3>
                <p className="text-muted-foreground mb-6">
                                  You need to create an account or sign in to upload documents and create book indexes.
              </p>
              <Button 
                onClick={() => setIsAuthModalOpen(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Sign In or Create Account
              </Button>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 text-destructive p-3 rounded-md flex items-start gap-2 mb-4">
                  <AlertCircle size={16} className="flex-shrink-0 mt-1" />
                  <span className="text-sm break-words">{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="project-name" className="text-sm font-medium text-foreground">
                    Book Title *
                  </Label>
                  <Input
                    id="project-name"
                    type="text"
                    value={projectName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
                    placeholder="Enter the title of your book"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project-description" className="text-sm font-medium text-foreground">
                    Description (Optional)
                  </Label>
                  <Input
                    id="project-description"
                    type="text"
                    value={projectDescription}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectDescription(e.target.value)}
                    placeholder="Brief description of your book"
                  />
                </div>

                {file ? (
                  <div className="bg-muted/10 border border-border/30 p-4 rounded-md">
                    <div className="flex items-center gap-3 mb-2">
                      <FileUp className="text-muted-foreground flex-shrink-0" size={20} />
                      <span className="font-medium text-foreground break-all text-sm">{file.name}</span>
                    </div>
                    <div className="text-sm text-muted-foreground mb-4">
                      {formatFileSize(file.size)}
                    </div>
                    <Button 
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                      onClick={handleProcessDocument}
                      disabled={isUploading || !projectName.trim()}
                    >
                      {isUploading ? 
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Index...</> 
                        : 'Create Index'}
                    </Button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-border/30 rounded-md p-6 text-center">
                    <Upload className="mx-auto text-muted-foreground mb-4" size={32} />
                    <h3 className="text-foreground font-medium mb-2">Select your document</h3>
                    <p className="text-muted-foreground text-sm mb-4">Choose a PDF document to index</p>
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept=".pdf"
                      onChange={handleFileChange}
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="file-upload"
                      className={`inline-block px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      Select Document
                    </label>
                    <p className="mt-4 text-xs text-muted-foreground">Supported format: PDF only (Max 10MB)</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AuthModal 
        open={isAuthModalOpen} 
        onOpenChange={setIsAuthModalOpen} 
        defaultMode="login"
      />
    </>
  );
}; 